import {
  Box,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  Stack,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { TIER_LEVEL_CONVERSION } from '../../config/const';
import CheckMark from '../../svg/CheckMark';

export interface TiersCardItemDescriptions {
  size?: { xs?: number; sm?: number; md?: number; lg?: number };
  embeddedSize?: { xs?: number; md?: number; lg?: number; xl?: number };
  name: string;
  description: string;
  tier: string;
  priceMain: string;
  priceMinor?: string;
  features: { [x: string]: any[] };
}

export interface TiersCardItemProps extends TiersCardItemDescriptions {
  currentTier: string;
  recommendedTier: React.RefObject<string>;
  handlePurchaseButton: (tier: string) => void;
  openFeatModal: (title: string, description: string | any[]) => void;
  isEmbedded?: boolean;
}

const TiersCardItem: React.FC<TiersCardItemProps> = (props) => {
  const {
    size,
    name,
    description,
    priceMain,
    priceMinor,
    tier,
    currentTier,
    handlePurchaseButton,
    features,
    openFeatModal,
    recommendedTier,
    embeddedSize,
    isEmbedded,
  } = props;

  const isRecommended = tier === recommendedTier.current;
  const isCurrent = currentTier === tier;

  const getButtonText = () => {
    if (!currentTier) {
      return `Choose ${name}`;
    }
    if (TIER_LEVEL_CONVERSION[tier] === TIER_LEVEL_CONVERSION[currentTier]) {
      return 'My Current Plan';
    }
    return `Switch to ${name}`;
  };

  return (
    <Grid size={(isEmbedded ? embeddedSize : size) || { xs: 12, md: 6 }}>
      <Box
        sx={{
          height: '100%',
          position: 'relative',
          pt: '30px',
        }}
      >
        {isRecommended && (
          <Box
            sx={{
              width: '100%',
              height: '50px',
              backgroundColor: '#2542FB',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              top: '2px',
              left: 0,
              zIndex: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, Helvetica',
                fontSize: '12px',
                fontWeight: 500,
                color: 'white',
                letterSpacing: '0.17px',
                lineHeight: '130%',
                textAlign: 'center',
                mt: '-18px',
              }}
            >
              Most Popular
            </Typography>
          </Box>
        )}

        <Card
          variant='outlined'
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            boxShadow: isRecommended
              ? '0px 2px 12px rgba(0, 0, 0, 0.08)'
              : 'none',
            backgroundColor: isRecommended
              ? 'background.container-brand-2'
              : 'background.paper-elevation-1',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <CardContent
            sx={{
              p: '24px',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              gap: '24px',
            }}
          >
            <Stack sx={{ gap: '40px' }}>
              <Stack sx={{ gap: '4px', height: '83px' }}>
                <Typography
                  sx={{
                    color: 'primary.main',
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: '20.8px',
                    letterSpacing: '0.15px',
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  sx={{
                    color: 'primary.main',
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: '20.02px',
                    letterSpacing: '0.17px',
                    height: '53px',
                  }}
                >
                  {description}
                </Typography>
              </Stack>

              <Stack sx={{ gap: '11px' }}>
                <Stack
                  direction='row'
                  alignItems='flex-end'
                  sx={{ gap: '4px' }}
                >
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: 34,
                      fontWeight: 600,
                      lineHeight: '41.99px',
                      letterSpacing: '0.25px',
                    }}
                  >
                    {priceMain}
                  </Typography>
                  {priceMinor && (
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: '19.92px',
                        letterSpacing: '0.4px',
                      }}
                    >
                      {priceMinor}
                    </Typography>
                  )}
                </Stack>

                <Button
                  variant='contained'
                  color={isRecommended ? 'primary' : 'secondary'}
                  size='large'
                  fullWidth
                  disabled={isCurrent}
                  onClick={() => handlePurchaseButton(tier)}
                  sx={{
                    py: 1,
                    px: 2.75,
                    borderRadius: '8px',
                    textTransform: 'none',
                    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 500,
                      lineHeight: '26px',
                      letterSpacing: '0.46px',
                    }}
                  >
                    {getButtonText()}
                  </Typography>
                </Button>
              </Stack>
            </Stack>
            <Stack sx={{ gap: '7.51px', flex: 1 }}>
              {Object.entries(features).map(([key, value], ind) => (
                <Stack
                  key={`${tier}-feat-${ind}`}
                  direction='row'
                  alignItems='center'
                  sx={{
                    gap: '10.51px',
                    cursor: !!value?.length ? 'pointer' : 'auto',
                  }}
                  onClick={() => {
                    if (value.length) {
                      openFeatModal(key, value);
                    }
                  }}
                >
                  <CheckMark />
                  <Typography
                    sx={{
                      flex: 1,
                      color: 'text.primary',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '20.02px',
                      letterSpacing: '0.17px',
                    }}
                  >
                    {key}
                  </Typography>
                  {!!value.length && (
                    <InfoIcon
                      sx={{
                        fontSize: '16px',
                        color: 'grey.500',
                      }}
                    />
                  )}
                </Stack>
              ))}
            </Stack>
            {(tier === 'free' || tier === 'ultimate') && (
              <Typography
                align='center'
                sx={{
                  color: 'text.primary',
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: '19.92px',
                  letterSpacing: '0.4px',
                }}
              >
                *coming soon
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Grid>
  );
};

export default TiersCardItem;
